'use client'

import { AnyAction, createAsyncThunk, createSlice, ThunkDispatch } from '@reduxjs/toolkit'
import i18n from 'i18next'
import Router from 'next/router'

import { customersApi } from 'api/customers/customers'
import { trackSuccessfulLogin } from 'app/components/shared/Analytics/segment'
import { ChangePasswordFormValues, LoginFormValues, RegisterFormValues } from 'appTypes/auth'
import apiClient from 'helpers/clients/apiClient'
import { AppThunk } from 'store'
import { STORAGE_KEYS } from 'utils/constants'
import { getFromStorage, removeFromStorage } from 'utils/storage'

import { connectOrderToAccount, resetCart, resetOrder } from '../cart/cartSlice'
import { displaySnackbar } from '../snackbar/snackbarSlice'

interface InitialState {
  isLoading: boolean
  isError: boolean
  isResetPasswordLinkSent: boolean
  isNewPasswordSet: boolean
}

const initialState: InitialState = {
  isLoading: false,
  isError: false,
  isResetPasswordLinkSent: false,
  isNewPasswordSet: false,
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      state.isError = false
    },
    requestSuccess: (state) => {
      state.isLoading = false
      state.isError = false
    },
    requestFailure: (state) => {
      state.isLoading = false
      state.isError = true
    },
  },
})

export const { requestStart, requestSuccess, requestFailure } = authSlice.actions

export default authSlice.reducer

export const registerUser =
  (registerData: RegisterFormValues): AppThunk =>
  async (dispatch) => {
    dispatch(requestStart())

    // Create customer in DB
    // Check if email exists
    const lowerEmail = registerData.email?.toLowerCase()
    const customerExistsResponse = await apiClient.checkCustomerExists(lowerEmail)
    if (!customerExistsResponse.success || !customerExistsResponse.data) {
      throw new Error('Error on checking customer existence')
    }

    if (customerExistsResponse.data.exists) {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('emailAlreadyTaken'), isError: true }))
      return
    }

    // Create customer in DB call
    registerData.email = lowerEmail
    const createdCustomer = await customersApi.createCustomerProfile(registerData)
    if (!createdCustomer) {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
      return
    }

    // Create customer in Cognito
    let signUpResult: any | undefined
    const { Auth } = await import('@aws-amplify/auth')
    try {
      signUpResult = await Auth.signUp({
        username: lowerEmail,
        password: registerData.password,
        autoSignIn: {
          // optional - enables auto sign in after user is confirmed
          enabled: false,
        },
      })
    } catch (err: any) {
      dispatch(requestFailure())

      let errorMessage

      if (err.code === 'CodeDeliveryFailureException') {
        try {
          await Auth.resendSignUp(lowerEmail)
        } catch (err) {
          errorMessage = i18n.t('error')
        }
      } else if (err.code === 'UsernameExistsException') {
        // The error happens when username is already takes
        errorMessage = i18n.t('emailAlreadyTaken')
      } else if (err.code === 'InvalidPasswordException') {
        errorMessage = i18n.t('passwordValidation')
      } else {
        errorMessage = i18n.t('error')
      }

      if (errorMessage) {
        dispatch(displaySnackbar({ message: errorMessage, isError: true }))
        // Rollback DB customer
        await customersApi.cleanupCustomerProfile(createdCustomer.id)
        return
      }
    }

    // Complete DB Customer
    await customersApi.completeCustomerProfile(createdCustomer.id, {
      uid: signUpResult.userSub,
    })

    dispatch(requestSuccess())
    Router.push('/register/confirm')
  }

export const loginCustomer =
  (loginData: LoginFormValues): AppThunk<Promise<{ cognitoCustomer: any } | undefined>> =>
  async (dispatch): Promise<{ cognitoCustomer: any } | undefined> => {
    dispatch(requestStart())

    try {
      const lowerEmail = loginData.email?.toLowerCase()
      const customerExistsResponse = await apiClient.checkCustomerExists(lowerEmail)
      if (!customerExistsResponse.success || !customerExistsResponse.data) {
        throw new Error('Error on checking customer existence')
      }
      const customerExistsInfo = customerExistsResponse.data
      if (customerExistsInfo.exists && customerExistsInfo.changePassword) {
        Router.push(`/login/reset-password?reset=true&email=${loginData.email}`)
        return undefined
      }

      const { Auth } = await import('@aws-amplify/auth')
      const customer = await Auth.signIn(lowerEmail, loginData.password)
      return { cognitoCustomer: customer }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.log('Error', err)
      let errorMessage
      if (err.code === 'UserNotConfirmedException') {
        errorMessage = i18n.t('common:accountNotConfirmed')
      } else if (err.code === 'NotAuthorizedException' || err.code === 'UserNotFoundException') {
        errorMessage = i18n.t('common:invalidLoginCredentials')
      } else {
        errorMessage = i18n.t('error')
      }

      if (errorMessage) {
        dispatch(
          displaySnackbar({
            message: errorMessage,
            isError: true,
          }),
        )
      }

      return undefined
    } finally {
      dispatch(requestSuccess())
    }
  }

const calculateRedirectUrl = async (
  redirectTo: string | null,
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
): Promise<string> => {
  const orderId = Number(getFromStorage(STORAGE_KEYS.orderId))
  const orderToken = JSON.parse(getFromStorage(STORAGE_KEYS.orderToken))

  if (orderId && orderToken) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const redirectResult = await dispatch(connectOrderToAccount(orderId) as any)
    removeFromStorage(STORAGE_KEYS.orderToken)

    dispatch(resetOrder())
    dispatch(resetCart())

    if (redirectResult) {
      return redirectResult
    }
  }

  if (redirectTo) {
    return redirectTo
  }

  return '/browse'
}

export const finishLogin = createAsyncThunk<
  void,
  { redirectTo: string | null; signIn: () => Promise<boolean>; pathname: string }
>('finishLogin', async ({ redirectTo, signIn, pathname }, { dispatch }) => {
  const signInResult = await signIn()
  if (!signInResult) {
    return
  }

  await trackSuccessfulLogin(pathname)

  const redirectUrl = await calculateRedirectUrl(redirectTo, dispatch)
  console.log(`finishLogin: redirectUrl`, redirectUrl)
  Router.push(redirectUrl)
})

export const completeNewPassword = createAsyncThunk<void, { customer: any; newPassword: string }>(
  'completeNewPassword',
  async ({ customer, newPassword }) => {
    const { Auth } = await import('@aws-amplify/auth')
    await Auth.completeNewPassword(customer, newPassword)
  },
)

export const logoutCustomer =
  (signOut?: () => Promise<void>): AppThunk<Promise<void>> =>
  async (dispatch): Promise<void> => {
    try {
      // Remove organization cookie if any
      await apiClient.selectOrganizationOrPersonalAccount(undefined)
      // Remove coordinates cookes is any
      await apiClient.setCustomerCoordinates(undefined)

      const { Auth } = await import('@aws-amplify/auth')
      await Auth.signOut()

      if (signOut) {
        await signOut()
      }

      // Remove from local store
      removeFromStorage(STORAGE_KEYS.orderId)
      removeFromStorage(STORAGE_KEYS.orderToken)
      removeFromStorage(STORAGE_KEYS.userToken)
      removeFromStorage(STORAGE_KEYS.shouldRememberCustomer)

      window.location.reload()
    } catch {
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
  }

export const isAuthenticatedCustomer =
  (): AppThunk<Promise<boolean>> =>
  async (dispatch): Promise<boolean> => {
    dispatch(requestStart())
    try {
      const { Auth } = await import('@aws-amplify/auth')
      const customer = await Auth.currentAuthenticatedUser()
      if (customer) {
        return true
      }
      return false
    } catch (err) {
      // Error happened - we are not logged in
      return false
    } finally {
      dispatch(requestSuccess())
    }
  }

export const getAuthenticatedCustomer =
  (): AppThunk<Promise<any | null>> =>
  async (dispatch): Promise<any | null> => {
    dispatch(requestStart())
    try {
      const { Auth } = await import('@aws-amplify/auth')
      const customer = await Auth.currentAuthenticatedUser()
      if (customer) {
        return customer
      }
      console.log(`getAuthenticatedCustomer: empty customer`, customer)
      return null
    } catch (err) {
      console.log(`getAuthenticatedCustomer: Error, we are not logged in`, err)
      // Error happened - we are not logged in
      return null
    } finally {
      dispatch(requestSuccess())
    }
  }

export const changePassword = createAsyncThunk<void, { values: ChangePasswordFormValues }>(
  'changePassword',
  async ({ values }, { dispatch }) => {
    dispatch(requestStart())

    try {
      const { Auth } = await import('@aws-amplify/auth')
      const customer = await Auth.currentAuthenticatedUser()
      await Auth.changePassword(customer, values.oldPassword, values.password)

      Router.push('/change-password/success')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let errorMessage
      if (err.code === 'NotAuthorizedException') {
        // The error happens when the old incorrect password is provided
        errorMessage = i18n.t('auth:oldPasswordInvalid')
      } else {
        errorMessage = i18n.t('error')
      }

      if (errorMessage) {
        dispatch(
          displaySnackbar({
            message: errorMessage,
            isError: true,
          }),
        )
      }
    } finally {
      dispatch(requestSuccess())
    }
  },
)

export const sendResetPasswordLink = createAsyncThunk<void, { email: string; callback: () => void }>(
  'sendResetPasswordLink',
  async ({ email, callback }, { dispatch }) => {
    dispatch(requestStart())

    try {
      const lowerEmail = email?.toLowerCase()
      const { Auth } = await import('@aws-amplify/auth')
      await Auth.forgotPassword(lowerEmail)
      callback()

      dispatch(requestSuccess())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let errorMessage

      if (err.code === 'LimitExceededException') {
        errorMessage = i18n.t('auth:limitExceededException')
      } else {
        errorMessage = i18n.t('error')
      }

      if (errorMessage) {
        dispatch(
          displaySnackbar({
            message: errorMessage,
            isError: true,
          }),
        )
      }

      dispatch(requestFailure())
    }
  },
)

export const setNewPassword = createAsyncThunk<void, { email: string; code: string; password: string }>(
  'setNewPassword',
  async ({ email, code, password }, { dispatch }) => {
    dispatch(requestStart())

    try {
      const lowerEmail = email?.toLowerCase()
      const { Auth } = await import('@aws-amplify/auth')
      await Auth.forgotPasswordSubmit(lowerEmail, code, password)
      await apiClient.setChangePassword(lowerEmail, false)
      Router.push(`/change-password/success`)

      dispatch(requestSuccess())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let errorMessage

      if (err.code === 'CodeMismatchException') {
        errorMessage = i18n.t('auth:codeMismatch')
      } else if (err.code === 'InvalidPasswordException') {
        errorMessage = i18n.t('auth:incorrectPassword')
      } else {
        errorMessage = i18n.t('error')
      }

      if (errorMessage) {
        dispatch(
          displaySnackbar({
            message: errorMessage,
            isError: true,
          }),
        )
      }

      dispatch(requestFailure())
    }
  },
)

export const confirmSignUp =
  (clientId: string, username: string, code: string): AppThunk<Promise<boolean>> =>
  async (dispatch): Promise<boolean> => {
    try {
      if (!clientId) {
        dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
        return false
      }

      const { Auth } = await import('@aws-amplify/auth')
      const response = await Auth.confirmSignUp(username, code)
      if (response) {
        return true
      }
      console.error(`Wrong response`, response)
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
      return false
    } catch (err) {
      console.error(`Error on signup`, err)
      // Error happened - we did not confirmed
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
      return false
    }
  }

export const federatedSignIn =
  (provider?: any, customProvider?: string): AppThunk<Promise<any | undefined>> =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async (dispatch): Promise<any | undefined> => {
    dispatch(requestStart())

    try {
      const { Auth } = await import('@aws-amplify/auth')
      if (provider) {
        await Auth.federatedSignIn({ provider: provider })
      } else if (customProvider) {
        await Auth.federatedSignIn({ customProvider: 'Vipps' })
      } else {
        throw new Error('Unknown provider')
      }
    } catch (err) {
      console.log('Error', err)
      dispatch(
        displaySnackbar({
          message: i18n.t('error'),
          isError: true,
        }),
      )
    } finally {
      dispatch(requestSuccess())
    }
  }

export const federatedSignInAgain = async (provider?: any, customProvider?: string): Promise<any | undefined> => {
  try {
    const { Auth } = await import('@aws-amplify/auth')
    if (provider) {
      await Auth.federatedSignIn({ provider: provider })
    } else if (customProvider) {
      await Auth.federatedSignIn({ customProvider: 'Vipps' })
    } else {
      throw new Error('Unknown provider')
    }
  } catch (err) {
    console.log('Error', err)
  }
}
